import { useState, useEffect } from 'react'
import { ApiService, FredConfig, YahooConfig, NewsSpider } from '../services/api'
import { useNavigate } from 'react-router-dom'
import ScheduleBuilder from '../components/ScheduleBuilder'
import SpiderDashboard from '../components/SpiderDashboard'
import SpiderDetailModal from '../components/SpiderDetailModal'
import UserManagement from '../components/UserManagement'

// Helper function to get user from localStorage
const getCurrentUser = () => {
  const userStr = localStorage.getItem('user')
  return userStr ? JSON.parse(userStr) : null
}

// Helper function to format date in EST
const formatDateEST = (dateString: string | null) => {
  if (!dateString) return 'Never'
  return new Date(dateString).toLocaleString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short'
  })
}

// Helper function to format date-only strings safely in EST
const formatDateOnlyEST = (dateString: string | null) => {
  if (!dateString) return 'N/A'
  
  try {
    // Convert to string if it's not already
    const dateStr = String(dateString)
    
    // For UTC dates like "2025-06-20T00:00:00.000Z", extract date part directly
    if (dateStr.includes('T00:00:00.000Z') || dateStr.includes('T00:00:00Z')) {
      const datePart = dateStr.split('T')[0] // Get "2025-06-20"
      const [year, month, day] = datePart.split('-').map(Number)
      const date = new Date(year, month - 1, day) // Create local date
      return date.toLocaleDateString('en-US')
    }
    
    // For other datetime strings, handle normally
    if (dateStr.includes('T')) {
      const date = new Date(dateStr)
      return date.toLocaleDateString('en-US', {
        timeZone: 'America/New_York'
      })
    } 
    
    // For date-only strings in YYYY-MM-DD format
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-').map(Number)
      const date = new Date(year, month - 1, day) // month is 0-indexed
      return date.toLocaleDateString('en-US')
    }
    
    // Try parsing as-is for other formats
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) {
      return 'Invalid Date'
    }
    return date.toLocaleDateString('en-US', {
      timeZone: 'America/New_York'
    })
    
  } catch (error) {
    console.error('Date formatting error:', { error, dateString })
    return 'Invalid Date'
  }
}

export default function AdminPage() {
  const navigate = useNavigate()
  const [user] = useState(getCurrentUser())
  const [fredConfig, setFredConfig] = useState<FredConfig | null>(null)
  const [yahooConfig, setYahooConfig] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('dashboard')
  const [activeApiTab, setActiveApiTab] = useState('fred')

  // FRED Form states
  const [newSeries, setNewSeries] = useState({ id: '', title: '', category: 'inflation' })
  const [newSchedule, setNewSchedule] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Yahoo Finance Form states
  const [newSymbol, setNewSymbol] = useState({ symbol: '', name: '', category: 'stock' })
  const [newYahooSchedule, setNewYahooSchedule] = useState('')

  // Yahoo Finance Backfill states
  const [showYahooBackfillModal, setShowYahooBackfillModal] = useState(false)
  const [yahooBackfillPassword, setYahooBackfillPassword] = useState('')
  const [isYahooBackfilling, setIsYahooBackfilling] = useState(false)

  // Backfill states
  const [showBackfillModal, setShowBackfillModal] = useState(false)
  const [backfillPassword, setBackfillPassword] = useState('')
  const [backfillStatus, setBackfillStatus] = useState<any>(null)
  const [isBackfilling, setIsBackfilling] = useState(false)

  // Yahoo Finance state
  const [yahooBackfillLoading, setYahooBackfillLoading] = useState(false)
  const [yahooBackfillError, setYahooBackfillError] = useState<string | null>(null)
  const [yahooBackfillSuccess, setYahooBackfillSuccess] = useState<string | null>(null)

  // Legacy Scraper state
  const [legacyScraperStatus, setLegacyScraperStatus] = useState<any>(null)
  const [legacyScraperLoading, setLegacyScraperLoading] = useState(false)
  const [legacyScraperError, setLegacyScraperError] = useState<string | null>(null)
  const [legacyScraperSuccess, setLegacyScraperSuccess] = useState<string | null>(null)
  const [legacyScraperOutputs, setLegacyScraperOutputs] = useState<any>(null)
  const [legacyScraperPassword, setLegacyScraperPassword] = useState('')

  // Legacy Scraper scheduling state
  const [legacyScraperSchedule, setLegacyScraperSchedule] = useState<any>(null)
  const [legacyScraperScheduleLoading, setLegacyScraperScheduleLoading] = useState(false)
  const [legacyScraperNewSchedule, setLegacyScraperNewSchedule] = useState('')
  const [legacyScraperEnabled, setLegacyScraperEnabled] = useState(false)

  // News Spiders state
  const [selectedSpider, setSelectedSpider] = useState<any>(null)
  const [showSpiderModal, setShowSpiderModal] = useState(false)
  const [showBulkTriggerModal, setShowBulkTriggerModal] = useState(false)
  const [selectedSpiderIds, setSelectedSpiderIds] = useState<string[]>([])
  const [bulkTriggerPassword, setBulkTriggerPassword] = useState('')
  const [bulkTriggerLoading, setBulkTriggerLoading] = useState(false)
  const [newsSpidersError, setNewsSpidersError] = useState<string | null>(null)
  const [newsSpidersSuccess, setNewsSpidersSuccess] = useState<string | null>(null)

  // Check admin access
  useEffect(() => {
    const loadData = async () => {
      if (!user || user.role !== 'admin') {
        navigate('/login')
        return
      }

      setLoading(true)
      try {
        await Promise.all([
          loadFredConfig(),
          loadBackfillStatus(),
          loadYahooConfig(),
          fetchLegacyScraperStatus(),
          fetchLegacyScraperOutputs(),
          fetchLegacyScraperSchedule()
        ])
      } catch (error) {
        console.error('Failed to load admin data:', error)
        setError('Failed to load admin data')
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [user, navigate])

  const loadFredConfig = async () => {
    try {
      setLoading(true)
      setError(null)
      const config = await ApiService.getFredConfig()
      setFredConfig(config)
      setNewSchedule(config.agent.schedule)
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load FRED configuration')
    } finally {
      setLoading(false)
    }
  }

  const loadBackfillStatus = async () => {
    try {
      const status = await ApiService.getFredBackfillStatus()
      setBackfillStatus(status)
    } catch (err: any) {
      console.error('Failed to load backfill status:', err)
      // Set default values if API call fails
      setBackfillStatus({
        totalRecords: 0,
        seriesCount: 0,
        dataRange: { earliest: null, latest: null },
        lastExecution: null
      })
    }
  }

  const loadYahooConfig = async () => {
    try {
      setLoading(true)
      setError(null)
      const config = await ApiService.getYahooConfig()
      setYahooConfig(config)
      setNewYahooSchedule(config.agent.schedule)
    } catch (err: any) {
      // If Yahoo Finance agent not found, try to initialize it
      if (err.response?.status === 404) {
        try {
          console.log('Yahoo Finance agent not found, initializing...')
          await ApiService.initializeYahooAgent()
          // Retry loading config after initialization
          const config = await ApiService.getYahooConfig()
          setYahooConfig(config)
          setNewYahooSchedule(config.agent.schedule)
          setError(null) // Clear any previous errors
        } catch (initErr: any) {
          setError(initErr.response?.data?.message || 'Failed to initialize Yahoo Finance configuration')
        }
      } else {
        setError(err.response?.data?.message || 'Failed to load Yahoo Finance configuration')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleToggleAgent = async () => {
    if (!fredConfig) return
    
    try {
      setIsSubmitting(true)
      await ApiService.updateFredStatus(!fredConfig.agent.enabled)
      await loadFredConfig()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update agent status')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateSchedule = async () => {
    if (!newSchedule.trim()) return

    try {
      setIsSubmitting(true)
      await ApiService.updateFredSchedule(newSchedule)
      await loadFredConfig()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update schedule')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddSeries = async () => {
    if (!newSeries.id.trim() || !newSeries.title.trim()) return

    try {
      setIsSubmitting(true)
      await ApiService.addFredSeries(newSeries)
      setNewSeries({ id: '', title: '', category: 'inflation' })
      await loadFredConfig()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add series')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRemoveSeries = async (seriesId: string) => {
    if (!confirm(`Are you sure you want to remove series ${seriesId}?`)) return

    try {
      setIsSubmitting(true)
      await ApiService.removeFredSeries(seriesId)
      await loadFredConfig()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove series')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleBackfillSubmit = async () => {
    if (!backfillPassword.trim()) return

    try {
      setIsBackfilling(true)
      setError(null)
      
      // Check if we still have a valid token
      const token = localStorage.getItem('auth_token')
      
      if (!token) {
        setError('Authentication expired. Please refresh the page and log in again.')
        setShowBackfillModal(false)
        setBackfillPassword('')
        return
      }
      
      const result = await ApiService.triggerFredBackfill(backfillPassword)
      
      if (result.success) {
        setShowBackfillModal(false)
        setBackfillPassword('')
        await loadBackfillStatus()
        alert('Historical backfill initiated successfully! The process is now running in the background and may take several minutes to complete. You can check the status below.')
      }
    } catch (err: any) {
      // Check if it's an authentication error
      if (err.response?.status === 401) {
        const errorMessage = err.response?.data?.message || 'Authentication failed'
        
        // Don't clear tokens for invalid password - that's just a wrong password
        if (errorMessage.includes('Invalid password')) {
          setError('Incorrect password. Please enter your correct user password.')
        } else {
          setError('Authentication failed. Your session may have expired. Please refresh the page and try again.')
          // Only clear tokens if it's a session issue, not wrong password
          localStorage.removeItem('auth_token')
          localStorage.removeItem('user')
        }
        setShowBackfillModal(false)
        setBackfillPassword('')
      } else {
        setError(err.response?.data?.message || 'Failed to initiate historical backfill')
      }
      console.error('Backfill error:', err)
    } finally {
      setIsBackfilling(false)
    }
  }

  // Yahoo Finance handlers
  const handleToggleYahooAgent = async () => {
    if (!yahooConfig) return
    
    try {
      setIsSubmitting(true)
      await ApiService.updateYahooStatus(!yahooConfig.agent.enabled)
      await loadYahooConfig()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update Yahoo Finance agent status')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleUpdateYahooSchedule = async () => {
    if (!newYahooSchedule.trim()) return

    try {
      setIsSubmitting(true)
      await ApiService.updateYahooSchedule(newYahooSchedule)
      await loadYahooConfig()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update Yahoo Finance schedule')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleAddSymbol = async () => {
    if (!newSymbol.symbol.trim() || !newSymbol.name.trim()) return

    try {
      setIsSubmitting(true)
      await ApiService.addYahooSymbol(newSymbol)
      setNewSymbol({ symbol: '', name: '', category: 'stock' })
      await loadYahooConfig()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to add symbol')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleRemoveSymbol = async (symbol: string) => {
    if (!confirm(`Are you sure you want to remove symbol ${symbol}?`)) return

    try {
      setIsSubmitting(true)
      await ApiService.removeYahooSymbol(symbol)
      await loadYahooConfig()
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove symbol')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleYahooBackfillSubmit = async () => {
    if (!yahooBackfillPassword.trim()) return

    try {
      setIsYahooBackfilling(true)
      setError(null)
      
      // Check if we still have a valid token
      const token = localStorage.getItem('auth_token')
      
      if (!token) {
        setError('Authentication expired. Please refresh the page and log in again.')
        setShowYahooBackfillModal(false)
        setYahooBackfillPassword('')
        return
      }
      
      const result = await ApiService.triggerYahooBackfill(yahooBackfillPassword)
      
      if (result.success) {
        setShowYahooBackfillModal(false)
        setYahooBackfillPassword('')
        await loadYahooConfig()
        alert('Historical backfill initiated successfully! The process is now running in the background and may take several minutes to complete. You can check the status below.')
      }
    } catch (err: any) {
      // Check if it's an authentication error
      if (err.response?.status === 401) {
        const errorMessage = err.response?.data?.message || 'Authentication failed'
        
        // Don't clear tokens for invalid password - that's just a wrong password
        if (errorMessage.includes('Invalid password')) {
          setError('Incorrect password. Please enter your correct user password.')
        } else {
          setError('Authentication failed. Your session may have expired. Please refresh the page and try again.')
          // Only clear tokens if it's a session issue, not wrong password
          localStorage.removeItem('auth_token')
          localStorage.removeItem('user')
        }
        setShowYahooBackfillModal(false)
        setYahooBackfillPassword('')
      } else {
        setError(err.response?.data?.message || 'Failed to initiate historical backfill')
      }
      console.error('Yahoo Finance backfill error:', err)
    } finally {
      setIsYahooBackfilling(false)
    }
  }

  // Fetch Yahoo Finance backfill status
  const fetchYahooBackfillStatus = async () => {
    try {
      const status = await ApiService.getYahooBackfillStatus()
      setYahooConfig((prev: any) => ({
        ...prev,
        dataStats: status
      }))
    } catch (error: any) {
      console.error('Failed to fetch Yahoo backfill status:', error)
    }
  }

  // Legacy Scraper functions
  const fetchLegacyScraperStatus = async () => {
    try {
      const status = await ApiService.getLegacyScraperStatus()
      setLegacyScraperStatus(status)
    } catch (error: any) {
      console.error('Failed to fetch legacy scraper status:', error)
      setLegacyScraperError('Failed to fetch scraper status')
    }
  }



  const fetchLegacyScraperOutputs = async (date?: string) => {
    try {
      const outputs = await ApiService.getLegacyScraperOutputs(date)
      setLegacyScraperOutputs(outputs)
    } catch (error: any) {
      console.error('Failed to fetch legacy scraper outputs:', error)
    }
  }

  const fetchLegacyScraperSchedule = async () => {
    try {
      const schedule = await ApiService.getLegacyScraperSchedule()
      setLegacyScraperSchedule(schedule.data)
      setLegacyScraperNewSchedule(schedule.data.schedule)
      setLegacyScraperEnabled(schedule.data.enabled)
    } catch (error: any) {
      console.error('Failed to fetch legacy scraper schedule:', error)
    }
  }

  const handleLegacyScraperTrigger = async () => {
    if (!legacyScraperPassword.trim()) {
      setLegacyScraperError('Password is required')
      return
    }

    setLegacyScraperLoading(true)
    setLegacyScraperError(null)
    setLegacyScraperSuccess(null)

    try {
      const result = await ApiService.triggerLegacyScraper(legacyScraperPassword)
      setLegacyScraperSuccess(result.message || 'Legacy scraper triggered successfully')
      setLegacyScraperPassword('')
      
      // Refresh status after a short delay
      setTimeout(() => {
        fetchLegacyScraperStatus()
      }, 2000)
      
    } catch (error: any) {
      setLegacyScraperError(error.response?.data?.error || 'Failed to trigger legacy scraper')
    } finally {
      setLegacyScraperLoading(false)
    }
  }

  const handleLegacyScraperScheduleUpdate = async () => {
    if (!legacyScraperNewSchedule.trim()) {
      setLegacyScraperError('Schedule is required')
      return
    }

    setLegacyScraperScheduleLoading(true)
    setLegacyScraperError(null)
    setLegacyScraperSuccess(null)

    try {
      const result = await ApiService.updateLegacyScraperSchedule(legacyScraperNewSchedule, legacyScraperEnabled)
      setLegacyScraperSuccess(result.message || 'Schedule updated successfully')
      
      // Refresh schedule
      await fetchLegacyScraperSchedule()
      
    } catch (error: any) {
      setLegacyScraperError(error.response?.data?.error || 'Failed to update schedule')
    } finally {
      setLegacyScraperScheduleLoading(false)
    }
  }

  // News Spiders handlers
  const handleSpiderClick = (spider: NewsSpider) => {
    setSelectedSpider(spider)
    setShowSpiderModal(true)
  }

  const handleSpiderUpdate = (updatedSpider: NewsSpider) => {
    setSelectedSpider(updatedSpider)
    // Could trigger a refresh of the spider dashboard here if needed
  }

  const handleBulkTrigger = (selectedSpiderIds: string[]) => {
    setSelectedSpiderIds(selectedSpiderIds)
    setShowBulkTriggerModal(true)
    setBulkTriggerPassword('')
    setNewsSpidersError(null)
    setNewsSpidersSuccess(null)
  }

  const handleBulkTriggerSubmit = async () => {
    if (!bulkTriggerPassword.trim()) {
      setNewsSpidersError('Password is required')
      return
    }

    if (selectedSpiderIds.length === 0) {
      setNewsSpidersError('No spiders selected')
      return
    }

    try {
      setBulkTriggerLoading(true)
      setNewsSpidersError(null)
      setNewsSpidersSuccess(null)

      // Get spider configurations for the selected spiders
      const spiderConfig = await ApiService.getNewsSpidersConfig()
      const selectedSpiders = spiderConfig.spiders
        .filter(spider => selectedSpiderIds.includes(spider.id))
        .map(spider => ({
          id: spider.id,
          maxArticles: spider.maxArticles,
          selectedCategories: spider.selectedCategories || []
        }))

      const result = await ApiService.triggerBulkSpiders(bulkTriggerPassword, selectedSpiders)
      
      setNewsSpidersSuccess(`Successfully triggered ${selectedSpiders.length} spiders! Job ID: ${result.data.jobId}`)
      setShowBulkTriggerModal(false)
      setBulkTriggerPassword('')
      
    } catch (error: any) {
      setNewsSpidersError(error.response?.data?.message || 'Failed to trigger bulk spiders')
    } finally {
      setBulkTriggerLoading(false)
    }
  }

  const mainTabs = [
    { id: 'dashboard', name: 'Dashboard' },
    { id: 'api-config', name: 'API Configuration' },
    { id: 'users', name: 'User Management' },
    { id: 'system', name: 'System Settings' }
  ]

  const apiTabs = [
    { id: 'fred', name: 'FRED Economic Data' },
    { id: 'yahoo', name: 'Yahoo Finance' },
    { id: 'legacy-scraper', name: 'Legacy Scraper' },
    { id: 'news-spiders', name: 'News Spiders' }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Panel</h1>
              <p className="text-gray-600">System administration and configuration</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-gray-500 hover:text-gray-700"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
              </div>
              <button 
                onClick={() => setError(null)}
                className="ml-auto text-red-400 hover:text-red-600"
              >
                ×
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Navigation Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            {mainTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">System Overview</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Platform Status:</span>
                  <span className="font-medium text-green-600">Operational</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Time (EST):</span>
                  <span className="font-medium">{new Date().toLocaleString('en-US', {
                    timeZone: 'America/New_York',
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZoneName: 'short'
                  })}</span>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <button
                  onClick={() => setActiveTab('api-config')}
                  className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
                >
                  Configure APIs
                </button>
                <button
                  onClick={() => setActiveTab('users')}
                  className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
                >
                  Manage Users
                </button>
                <button
                  onClick={() => setActiveTab('system')}
                  className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
                >
                  System Settings
                </button>
              </div>
            </div>
          </div>
        )}

        {/* API Configuration Tab */}
        {activeTab === 'api-config' && (
          <div>
            {/* API Sub-navigation */}
            <div className="mb-6">
              <div className="border-b border-gray-200">
                <nav className="-mb-px flex space-x-8">
                  {apiTabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveApiTab(tab.id)}
                      className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                        activeApiTab === tab.id
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {tab.name}
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* FRED API Configuration */}
            {activeApiTab === 'fred' && (
              <div>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading FRED configuration...</p>
                    </div>
                  </div>
                ) : !fredConfig ? (
                  <div className="text-center py-12">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Configuration Not Found</h2>
                    <p className="text-gray-600 mb-4">FRED configuration could not be loaded</p>
                    <button 
                      onClick={loadFredConfig}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                    >
                      Retry
                    </button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Historical Data Status */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Historical Data Management</h3>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{backfillStatus?.totalRecords?.toLocaleString() || 0}</div>
                          <div className="text-sm text-gray-500">Total Records</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{backfillStatus?.seriesCount || 0}</div>
                          <div className="text-sm text-gray-500">Active Series</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">
                            {backfillStatus?.dataRange?.earliest ? new Date(backfillStatus.dataRange.earliest).getFullYear() : 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">Earliest Data</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">
                            {formatDateOnlyEST(backfillStatus?.dataRange?.latest)}
                          </div>
                          <div className="text-sm text-gray-500">Latest Data</div>
                        </div>
                      </div>
                      
                                              <div className="mt-4 pt-4 border-t">
                          <button
                            onClick={() => setShowBackfillModal(true)}
                            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 font-medium"
                          >
                            Restore All Historical Values
                          </button>
                          <p className="text-xs text-gray-500 mt-1">
                            This will fetch complete historical data for all economic indicators (requires password)
                          </p>
                        </div>
                    </div>

                    {/* FRED Overview */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Status Card */}
                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Agent Status</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Status:</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              fredConfig.agent.enabled 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {fredConfig.agent.enabled ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Schedule:</span>
                            <span className="font-mono text-sm">{fredConfig.agent.schedule}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Data Source:</span>
                            <span className="font-medium">{fredConfig.agent.dataSource}</span>
                          </div>
                          <div className="pt-3">
                            <button
                              onClick={handleToggleAgent}
                              disabled={isSubmitting}
                              className={`w-full px-4 py-2 rounded-md font-medium ${
                                fredConfig.agent.enabled
                                  ? 'bg-red-100 text-red-800 hover:bg-red-200'
                                  : 'bg-green-100 text-green-800 hover:bg-green-200'
                              } disabled:opacity-50`}
                            >
                              {isSubmitting ? 'Updating...' : (fredConfig.agent.enabled ? 'Disable Agent' : 'Enable Agent')}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Statistics Card */}
                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Execution Statistics</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Runs:</span>
                            <span className="font-medium">{fredConfig.statistics.totalExecutions}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Successful:</span>
                            <span className="font-medium text-green-600">{fredConfig.statistics.successfulExecutions}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Failed:</span>
                            <span className="font-medium text-red-600">{fredConfig.statistics.failedExecutions}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Avg Duration:</span>
                            <span className="font-medium">{Math.round(fredConfig.statistics.avgExecutionTime)}ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Last Run (EST):</span>
                            <span className="font-medium text-sm">
                              {formatDateEST(fredConfig.statistics.lastExecution)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Series Overview Card */}
                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Series Overview</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Series:</span>
                            <span className="font-medium">{fredConfig.series.total}</span>
                          </div>
                          <div className="space-y-2">
                            {Object.entries(
                              fredConfig.series.list.reduce((acc: Record<string, number>, series) => {
                                acc[series.category] = (acc[series.category] || 0) + 1
                                return acc
                              }, {})
                            ).map(([category, count]) => (
                              <div key={category} className="flex justify-between text-sm">
                                <span className="text-gray-500 capitalize">{category}:</span>
                                <span className="font-medium">{count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Series Management */}
                    <div className="space-y-6">
                      {/* Add New Series */}
                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Series</h3>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Series ID</label>
                            <input
                              type="text"
                              value={newSeries.id}
                              onChange={(e) => setNewSeries({...newSeries, id: e.target.value})}
                              placeholder="e.g., CPIAUCSL"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                            <input
                              type="text"
                              value={newSeries.title}
                              onChange={(e) => setNewSeries({...newSeries, title: e.target.value})}
                              placeholder="e.g., Consumer Price Index"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                            <select
                              value={newSeries.category}
                              onChange={(e) => setNewSeries({...newSeries, category: e.target.value})}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="inflation">Inflation</option>
                              <option value="employment">Employment</option>
                              <option value="gdp">GDP</option>
                              <option value="rates">Interest Rates</option>
                              <option value="markets">Markets</option>
                              <option value="housing">Housing</option>
                              <option value="consumer">Consumer</option>
                            </select>
                          </div>
                          <div className="flex items-end">
                            <button
                              onClick={handleAddSeries}
                              disabled={isSubmitting || !newSeries.id.trim() || !newSeries.title.trim()}
                              className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                            >
                              {isSubmitting ? 'Adding...' : 'Add Series'}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Current Series */}
                      <div className="bg-white rounded-lg shadow">
                        <div className="px-6 py-4 border-b border-gray-200">
                          <h3 className="text-lg font-medium text-gray-900">Current Series ({fredConfig.series.total})</h3>
                        </div>
                        <div className="divide-y divide-gray-200">
                          {fredConfig.series.list.map((series) => (
                            <div key={series.id} className="px-6 py-4 flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{series.id}</span>
                                  <span className="font-medium">{series.title}</span>
                                  <span className="text-sm text-gray-500 capitalize bg-blue-50 px-2 py-1 rounded">
                                    {series.category}
                                  </span>
                                </div>
                              </div>
                              <button
                                onClick={() => handleRemoveSeries(series.id)}
                                disabled={isSubmitting}
                                className="text-red-600 hover:text-red-800 disabled:opacity-50 px-2 py-1 text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Schedule Configuration */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-medium text-gray-900">Schedule Configuration</h3>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            fredConfig.agent.enabled 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {fredConfig.agent.enabled ? 'Active' : 'Inactive'}
                          </span>
                          <button
                            onClick={handleToggleAgent}
                            disabled={isSubmitting}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                              fredConfig.agent.enabled ? 'bg-blue-600' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                fredConfig.agent.enabled ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                      </div>

                      <ScheduleBuilder 
                        currentSchedule={fredConfig.agent.schedule}
                        onScheduleChange={setNewSchedule}
                        onUpdateSchedule={handleUpdateSchedule}
                        isSubmitting={isSubmitting}
                      />
                    </div>

                    {/* API Configuration */}
                    <div className="space-y-6">
                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">API Configuration</h3>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                            <div>
                              <p className="font-medium text-gray-900">FRED API Key</p>
                              <p className="text-sm text-gray-600">
                                Status: <span className={`font-mono ${
                                  fredConfig.apiKey.configured ? 'text-green-600' : 'text-red-600'
                                }`}>
                                  {fredConfig.apiKey.configured ? 'Configured' : 'Not Configured'}
                                </span>
                              </p>
                            </div>
                            <div className="text-2xl">
                              {fredConfig.apiKey.configured ? '✓' : '⚠'}
                            </div>
                          </div>
                          
                          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                            <div className="flex">
                              <div className="ml-3">
                                <h3 className="text-sm font-medium text-yellow-800">API Key Management</h3>
                                <p className="text-sm text-yellow-700 mt-1">
                                  API keys are managed through AWS Secrets Manager for security. 
                                  Contact system administrator to update the FRED API key.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Agent Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-3">
                            <div>
                              <span className="text-sm font-medium text-gray-600">Name:</span>
                              <p className="font-mono">{fredConfig.agent.name}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">Type:</span>
                              <p className="capitalize">{fredConfig.agent.type}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">Data Source:</span>
                              <p>{fredConfig.agent.dataSource}</p>
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <span className="text-sm font-medium text-gray-600">Created (EST):</span>
                              <p>{formatDateEST(fredConfig.agent.createdAt)}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">Last Updated (EST):</span>
                              <p>{formatDateEST(fredConfig.agent.updatedAt)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Yahoo Finance API Configuration */}
            {activeApiTab === 'yahoo' && (
              <div className="space-y-8">
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <p className="text-red-800">{error}</p>
                  </div>
                )}

                {loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading Yahoo Finance configuration...</p>
                  </div>
                ) : yahooConfig ? (
                  <div className="space-y-8">
                    {/* Historical Data Status */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Historical Data Management</h3>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-blue-600">{yahooConfig.dataStats?.totalRecords?.toLocaleString() || 0}</div>
                          <div className="text-sm text-gray-500">Total Records</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-600">{yahooConfig.dataStats?.symbolsCount || 0}</div>
                          <div className="text-sm text-gray-500">Active Symbols</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">
                            {yahooConfig.dataStats?.dataRange?.earliest ? new Date(yahooConfig.dataStats.dataRange.earliest).getFullYear() : 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">Earliest Data</div>
                        </div>
                        <div className="text-center">
                          <div className="text-sm font-medium text-gray-900">
                            {formatDateOnlyEST(yahooConfig.dataStats?.dataRange?.latest)}
                          </div>
                          <div className="text-sm text-gray-500">Latest Data</div>
                        </div>
                      </div>
                      
                      <div className="mt-4 pt-4 border-t">
                        <button
                          onClick={() => setShowYahooBackfillModal(true)}
                          className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 font-medium"
                        >
                          Restore All Historical Values
                        </button>
                        <p className="text-xs text-gray-500 mt-1">
                          This will fetch 5 years of historical data for all configured symbols (requires password)
                        </p>
                      </div>
                    </div>

                    {/* Yahoo Finance Overview */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                      {/* Status Card */}
                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Agent Status</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Status:</span>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              yahooConfig.agent.enabled 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {yahooConfig.agent.enabled ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Schedule:</span>
                            <span className="font-mono text-sm">{yahooConfig.agent.schedule}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Data Source:</span>
                            <span className="font-medium">{yahooConfig.agent.dataSource}</span>
                          </div>
                          <div className="pt-3">
                            <button
                              onClick={handleToggleYahooAgent}
                              disabled={isSubmitting}
                              className={`w-full px-4 py-2 rounded-md font-medium ${
                                yahooConfig.agent.enabled
                                  ? 'bg-red-100 text-red-800 hover:bg-red-200'
                                  : 'bg-green-100 text-green-800 hover:bg-green-200'
                              } disabled:opacity-50`}
                            >
                              {isSubmitting ? 'Updating...' : (yahooConfig.agent.enabled ? 'Disable Agent' : 'Enable Agent')}
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Statistics Card */}
                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Execution Statistics</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Runs:</span>
                            <span className="font-medium">{yahooConfig.statistics?.totalExecutions || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Successful:</span>
                            <span className="font-medium text-green-600">{yahooConfig.statistics?.successfulExecutions || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Failed:</span>
                            <span className="font-medium text-red-600">{yahooConfig.statistics?.failedExecutions || 0}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Avg Duration:</span>
                            <span className="font-medium">{Math.round(yahooConfig.statistics?.avgExecutionTime || 0)}ms</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Last Run (EST):</span>
                            <span className="font-medium text-sm">
                              {formatDateEST(yahooConfig.statistics?.lastExecution)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Symbols Overview Card */}
                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Symbols Overview</h3>
                        <div className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Total Symbols:</span>
                            <span className="font-medium">{yahooConfig.symbols.total}</span>
                          </div>
                          <div className="space-y-2">
                            {Object.entries(
                              yahooConfig.symbols.list.reduce((acc: Record<string, number>, symbol: any) => {
                                acc[symbol.category] = (acc[symbol.category] || 0) + 1
                                return acc
                              }, {})
                            ).map(([category, count]) => (
                              <div key={category} className="flex justify-between text-sm">
                                <span className="text-gray-500 capitalize">{category}:</span>
                                <span className="font-medium">{count as number}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Add New Symbol */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Symbol</h3>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Symbol</label>
                          <input
                            type="text"
                            value={newSymbol.symbol}
                            onChange={(e) => setNewSymbol(prev => ({ ...prev, symbol: e.target.value.toUpperCase() }))}
                            placeholder="AAPL"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                          <input
                            type="text"
                            value={newSymbol.name}
                            onChange={(e) => setNewSymbol(prev => ({ ...prev, name: e.target.value }))}
                            placeholder="Apple Inc."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                          <select
                            value={newSymbol.category}
                            onChange={(e) => setNewSymbol(prev => ({ ...prev, category: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="stock">Stock</option>
                            <option value="index">Index</option>
                            <option value="commodity">Commodity</option>
                            <option value="volatility">Volatility</option>
                            <option value="crypto">Cryptocurrency</option>
                            <option value="bond">Bond</option>
                            <option value="currency">Currency</option>
                          </select>
                        </div>
                        <div className="flex items-end">
                          <button
                            onClick={handleAddSymbol}
                            disabled={isSubmitting || !newSymbol.symbol.trim() || !newSymbol.name.trim()}
                            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                          >
                            {isSubmitting ? 'Adding...' : 'Add Symbol'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Current Symbols */}
                    <div className="bg-white rounded-lg shadow">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Current Symbols ({yahooConfig.symbols.total})</h3>
                      </div>
                      <div className="divide-y divide-gray-200">
                        {yahooConfig.symbols.list.map((symbol: any) => (
                          <div key={symbol.symbol} className="px-6 py-4 flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">{symbol.symbol}</span>
                                <span className="font-medium">{symbol.name}</span>
                                <span className="text-sm text-gray-500 capitalize bg-blue-50 px-2 py-1 rounded">
                                  {symbol.category}
                                </span>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveSymbol(symbol.symbol)}
                              disabled={isSubmitting}
                              className="text-red-600 hover:text-red-800 disabled:opacity-50 px-2 py-1 text-sm"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Schedule Configuration */}
                    <div className="bg-white rounded-lg shadow p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-medium text-gray-900">Schedule Configuration</h3>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            yahooConfig.agent.enabled 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {yahooConfig.agent.enabled ? 'Active' : 'Inactive'}
                          </span>
                          <button
                            onClick={handleToggleYahooAgent}
                            disabled={isSubmitting}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                              yahooConfig.agent.enabled ? 'bg-blue-600' : 'bg-gray-200'
                            }`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                yahooConfig.agent.enabled ? 'translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </div>
                      </div>

                      <ScheduleBuilder 
                        currentSchedule={yahooConfig.agent.schedule}
                        onScheduleChange={setNewYahooSchedule}
                        onUpdateSchedule={handleUpdateYahooSchedule}
                        isSubmitting={isSubmitting}
                      />
                    </div>

                    {/* API Configuration */}
                    <div className="space-y-6">
                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">API Configuration</h3>
                        <div className="space-y-4">
                          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-md">
                            <div>
                              <p className="font-medium text-gray-900">Yahoo Finance API</p>
                              <p className="text-sm text-gray-600">
                                Status: <span className="font-mono text-green-600">No API Key Required</span>
                              </p>
                            </div>
                            <div className="text-2xl">✓</div>
                          </div>
                          
                          <div className="bg-green-50 border border-green-200 rounded-md p-4">
                            <div className="flex">
                              <div className="ml-3">
                                <h3 className="text-sm font-medium text-green-800">Free API Access</h3>
                                <p className="text-sm text-green-700 mt-1">
                                  Yahoo Finance API is free to use and doesn't require an API key. 
                                  Data is fetched directly from Yahoo's public endpoints.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Agent Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-3">
                            <div>
                              <span className="text-sm font-medium text-gray-600">Name:</span>
                              <p className="font-mono">{yahooConfig.agent.name}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">Type:</span>
                              <p className="capitalize">{yahooConfig.agent.type}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">Data Source:</span>
                              <p>{yahooConfig.agent.dataSource}</p>
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <span className="text-sm font-medium text-gray-600">Created (EST):</span>
                              <p>{formatDateEST(yahooConfig.agent.createdAt)}</p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">Last Updated (EST):</span>
                              <p>{formatDateEST(yahooConfig.agent.updatedAt)}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Configuration Not Found</h2>
                    <p className="text-gray-600 mb-4">Yahoo Finance configuration could not be loaded</p>
                    <button 
                      onClick={loadYahooConfig}
                      className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Legacy Scraper Tab */}
            {activeApiTab === 'legacy-scraper' && (
              <div className="space-y-6">
                {/* Header */}
                <div className="bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        McCloskey/SOFR Quant Scraper (Legacy)
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Custom-built scraper for commodity prices and bond rates from legacy system
                      </p>
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        <span>Endpoint: https://scraper.corebriefing.com/scrape/legacy</span>
                        <span>Outputs: S3 Bucket</span>
                        <span>Logs: CloudWatch</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-orange-600">Legacy System</div>
                      <div className="text-xs text-gray-500">Custom API</div>
                    </div>
                  </div>
                </div>

                {/* Status Overview */}
                {legacyScraperStatus && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">Execution Statistics</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-blue-600">
                          {legacyScraperStatus.stats?.totalExecutions || 0}
                        </div>
                        <div className="text-sm text-gray-500">Total Runs</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">
                          {legacyScraperStatus.stats?.successfulExecutions || 0}
                        </div>
                        <div className="text-sm text-gray-500">Successful</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">
                          {legacyScraperStatus.stats?.failedExecutions || 0}
                        </div>
                        <div className="text-sm text-gray-500">Failed</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-purple-600">
                          {legacyScraperStatus.stats?.avgExecutionTime ? `${Math.round(legacyScraperStatus.stats.avgExecutionTime / 1000)}s` : 'N/A'}
                        </div>
                        <div className="text-sm text-gray-500">Avg Time</div>
                      </div>
                    </div>
                    
                    {legacyScraperStatus.stats?.lastExecution && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="text-sm text-gray-600">
                          Last execution: {formatDateEST(legacyScraperStatus.stats.lastExecution)}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Manual Trigger */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-md font-semibold text-gray-900 mb-4">Manual Trigger</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Manually trigger the legacy scraper to collect latest commodity prices and bond rates.
                    This will initiate the scraping process and save results to S3.
                  </p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Admin Password (Required)
                      </label>
                      <input
                        type="password"
                        value={legacyScraperPassword}
                        onChange={(e) => setLegacyScraperPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Enter your password"
                        disabled={legacyScraperLoading}
                      />
                    </div>
                    
                    <button
                      onClick={handleLegacyScraperTrigger}
                      disabled={legacyScraperLoading || !legacyScraperPassword.trim()}
                      className="w-full bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {legacyScraperLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                                                     Triggering Scraper...
                         </>
                       ) : (
                         'Trigger Legacy Scraper'
                      )}
                    </button>
                  </div>

                  {legacyScraperError && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-sm text-red-600">{legacyScraperError}</p>
                    </div>
                  )}

                  {legacyScraperSuccess && (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-600">{legacyScraperSuccess}</p>
                    </div>
                  )}
                </div>

                {/* Schedule Configuration */}
                {legacyScraperSchedule && (
                  <div className="bg-white rounded-lg shadow p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-medium text-gray-900">Schedule Configuration</h3>
                      <div className="flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          legacyScraperSchedule.enabled 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {legacyScraperSchedule.enabled ? 'Active' : 'Inactive'}
                        </span>
                        <button
                          onClick={() => setLegacyScraperEnabled(!legacyScraperEnabled)}
                          disabled={legacyScraperScheduleLoading}
                          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                            legacyScraperEnabled ? 'bg-blue-600' : 'bg-gray-200'
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              legacyScraperEnabled ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </div>
                    </div>

                    <ScheduleBuilder 
                      currentSchedule={legacyScraperSchedule.schedule}
                      onScheduleChange={setLegacyScraperNewSchedule}
                      onUpdateSchedule={handleLegacyScraperScheduleUpdate}
                      isSubmitting={legacyScraperScheduleLoading}
                    />
                  </div>
                )}

                {/* Recent Executions */}
                {legacyScraperStatus?.latestExecutions && legacyScraperStatus.latestExecutions.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">Recent Executions</h4>
                    <div className="space-y-2">
                      {legacyScraperStatus.latestExecutions.slice(0, 5).map((execution: any, index: number) => (
                        <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                          <div className="flex items-center space-x-3">
                            <span className={`w-2 h-2 rounded-full ${execution.success ? 'bg-green-500' : 'bg-red-500'}`}></span>
                            <span className="text-sm font-medium">
                              {execution.success ? 'Success' : 'Failed'}
                            </span>
                            {execution.error_message && (
                              <span className="text-xs text-red-600 truncate max-w-xs">
                                {execution.error_message}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDateEST(execution.executed_at)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* S3 Outputs */}
                {legacyScraperOutputs && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-semibold text-gray-900">S3 Output Files</h4>
                      <div className="text-sm text-gray-500">
                        Date: {legacyScraperOutputs.date} | Files: {legacyScraperOutputs.totalFiles}
                      </div>
                    </div>
                    
                    {legacyScraperOutputs.outputs.length > 0 ? (
                      <div className="space-y-2">
                        {legacyScraperOutputs.outputs.map((file: any, index: number) => (
                          <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded">
                            <div className="flex items-center space-x-3">
                              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                              <span className="text-sm font-medium">{file.key.split('/').pop()}</span>
                            </div>
                            <div className="text-xs text-gray-500">
                              {(file.size / 1024).toFixed(1)} KB | {formatDateEST(file.lastModified)}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4 text-gray-500">
                        No output files found for today. Run the scraper to generate new files.
                      </div>
                    )}
                  </div>
                )}



                {/* Footer Note */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="w-5 h-5 bg-yellow-500 rounded-full flex-shrink-0 mt-0.5 mr-3"></div>
                    <div className="text-sm">
                      <p className="font-medium text-yellow-800">Legacy System Notice</p>
                      <p className="text-yellow-700 mt-1">
                        This scraper is part of the legacy system and may be updated or replaced in future versions. 
                        It currently processes commodity prices and bond rates, saving results to S3 for further processing.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* News Spiders Tab */}
            {activeApiTab === 'news-spiders' && (
              <div className="space-y-6">
                {/* Header */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        News Spiders Coordination System
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">
                        Manage and coordinate 15 specialized web scraping spiders for news and articles
                      </p>
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        <span>API: https://scraper.corebriefing.com/scrape/news</span>
                        <span className="text-green-600 font-medium">Mode: Full Production</span>
                        <span>Categories: Dynamic</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-blue-600">Production Ready</div>
                      <div className="text-xs text-gray-500">15 Active Spiders</div>
                    </div>
                  </div>
                </div>

                {/* News Spiders Success/Error Messages */}
                {newsSpidersError && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Error</h3>
                        <p className="text-sm text-red-700 mt-1">{newsSpidersError}</p>
                      </div>
                      <button 
                        onClick={() => setNewsSpidersError(null)}
                        className="ml-auto text-red-400 hover:text-red-600"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}

                {newsSpidersSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-md p-4">
                    <div className="flex">
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-green-800">Success</h3>
                        <p className="text-sm text-green-700 mt-1">{newsSpidersSuccess}</p>
                      </div>
                      <button 
                        onClick={() => setNewsSpidersSuccess(null)}
                        className="ml-auto text-green-400 hover:text-green-600"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}

                {/* Spider Dashboard */}
                <SpiderDashboard 
                  onSpiderClick={handleSpiderClick}
                  onBulkTrigger={handleBulkTrigger}
                />

                {/* Spider Detail Modal */}
                {selectedSpider && (
                  <SpiderDetailModal
                    spider={selectedSpider}
                    isOpen={showSpiderModal}
                    onClose={() => {
                      setShowSpiderModal(false)
                      setSelectedSpider(null)
                    }}
                    onUpdate={handleSpiderUpdate}
                  />
                )}

                {/* Bulk Trigger Modal */}
                {showBulkTriggerModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                      <h3 className="text-lg font-medium text-gray-900 mb-4">Bulk Trigger Spiders</h3>
                      <div className="space-y-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                          <p className="text-sm text-blue-800">
                            <strong>Note:</strong> This will trigger all selected spiders simultaneously using their current configurations.
                            The operation may take several minutes to complete.
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Enter Your Password to Continue:
                          </label>
                          <input
                            type="password"
                            value={bulkTriggerPassword}
                            onChange={(e) => setBulkTriggerPassword(e.target.value)}
                            placeholder="Enter your password"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onKeyPress={(e) => e.key === 'Enter' && handleBulkTriggerSubmit()}
                          />
                        </div>
                        
                        <div className="flex justify-end space-x-3">
                          <button
                            onClick={() => {
                              setShowBulkTriggerModal(false)
                              setBulkTriggerPassword('')
                            }}
                            className="px-4 py-2 text-gray-600 hover:text-gray-800"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleBulkTriggerSubmit}
                            disabled={bulkTriggerLoading || !bulkTriggerPassword.trim()}
                            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
                          >
                            {bulkTriggerLoading ? 'Triggering...' : 'Trigger Spiders'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Info Panel */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <div className="w-5 h-5 bg-blue-500 rounded-full flex-shrink-0 mt-0.5 mr-3"></div>
                    <div className="text-sm">
                      <p className="font-medium text-blue-800">Spider Coordination System</p>
                      <p className="text-blue-700 mt-1">
                        This system coordinates 15 specialized spiders covering energy, financial, general news, and transportation sectors.
                        Each spider can be configured individually with custom categories, article limits, and schedules.
                        All spiders run in --mode full for comprehensive data extraction.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <UserManagement user={user} />
        )}

        {/* System Settings Tab */}
        {activeTab === 'system' && (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 mb-2">System Settings</h3>
            <p className="text-gray-600">System configuration options will be available here.</p>
          </div>
        )}
      </div>

      {/* Backfill Password Modal */}
      {showBackfillModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Historical Data Restore</h3>
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This operation will fetch ALL historical data for configured economic indicators. 
                  This may take several minutes and will download thousands of data points.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter Your Password to Continue:
                </label>
                <input
                  type="password"
                  value={backfillPassword}
                  onChange={(e) => setBackfillPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleBackfillSubmit()}
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowBackfillModal(false)
                    setBackfillPassword('')
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBackfillSubmit}
                  disabled={isBackfilling || !backfillPassword.trim()}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
                >
                  {isBackfilling ? 'Initiating...' : 'Start Backfill'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Yahoo Finance Backfill Password Modal */}
      {showYahooBackfillModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Confirm Historical Data Restore</h3>
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Warning:</strong> This operation will fetch 5 years of historical data for all configured symbols. 
                  This may take several minutes and will download thousands of data points from Yahoo Finance.
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter Your Password to Continue:
                </label>
                <input
                  type="password"
                  value={yahooBackfillPassword}
                  onChange={(e) => setYahooBackfillPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyPress={(e) => e.key === 'Enter' && handleYahooBackfillSubmit()}
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowYahooBackfillModal(false)
                    setYahooBackfillPassword('')
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleYahooBackfillSubmit}
                  disabled={isYahooBackfilling || !yahooBackfillPassword.trim()}
                  className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 font-medium"
                >
                  {isYahooBackfilling ? 'Fetching Data...' : 'Start Backfill'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
} 